/**
 * The station commissioning and management screens' rules (M6 plan P34, spec §7.2, §7.3, §8). Pure: the screens
 * render these and send the commands they build; the worker (`strategic/corp/commission.ts`, `stationOps.ts`,
 * `claims.ts`) stays the judge, and these mirror its refusals in its order so the screens explain them first.
 *
 * - Charter (`charterModel`, `buyCharter`): founded; a system held by another faction needs the Career's standing
 *   with the holder at `minStanding` (25) or more (unclaimed and own claims need none); at most `maxPerSystem`
 *   charters plus stations per system; charters plus stations within the corporation's cap (10); the company
 *   account covers the price.
 * - Commission (`commissionModel`, `commissionStation`): a charter in the system, a commission slot free, a level
 *   the type offers, a free approved site.
 * - Upgrade (`upgradeModel`, `upgradeStation`): built, no construction running, a next level.
 * - Price policy, permissions, manager, tariff and docking rights: drafts that round-trip to the commands. An automatic
 *   tariff reads "not set" and `denyFaction` reads "Closed (own ships only)" (D-424, M5 spec §27.4, §27.6).
 */
import { z } from 'zod'
import corpJson from '../../data/strategic/corp.json'
import {
  DOCKING_RIGHTS_NAMES,
  PRICE_POLICY_NAMES,
  TRADE_PERMISSION_NAMES,
  type CaptainSummary,
  type CommissionLevelView,
  type CommissionSiteView,
  type CommissionSystemView,
  type CommissionView,
  type DockingRightsName,
  type M6Command,
  type PlayerStationView,
  type PricePolicyName,
  type StationTypeName,
  type TradePermissionName,
} from '../../strategic/types'
import type { WareId } from '../../strategic/wares'
import { formatCr, formatPct } from '../format'
import screenJson from './stationsScreen.json'

const labels = z.record(z.string(), z.string())
const screenSchema = z.object({
  types: labels,
  anchors: labels,
  permissions: labels,
  docking: labels,
  policies: labels,
  priceStep: z.number().positive(),
  tariffStep: z.number().positive(),
  tariffUnset: z.string().min(1),
  stationCount: z.object({ one: z.string().min(1), many: z.string().min(1) }),
  earnings: z.string().min(1),
  refusals: labels,
})
const screen = screenSchema.parse(screenJson)
const tariffRange = z.tuple([z.number().min(0), z.number().min(0)]).parse(corpJson.claims.tariff)

export const PRICE_STEP: number = screen.priceStep
export const TARIFF_STEP: number = screen.tariffStep
export const TARIFF_RANGE: readonly [number, number] = tariffRange

export function typeLabel(type: StationTypeName): string {
  return screen.types[type] ?? type
}
export function anchorLabel(anchor: CommissionSiteView['anchor']): string {
  return screen.anchors[anchor] ?? anchor
}
export function permissionLabel(p: TradePermissionName): string {
  return screen.permissions[p] ?? p
}
export function dockingLabel(d: DockingRightsName): string {
  return screen.docking[d] ?? d
}
export function policyLabel(p: PricePolicyName): string {
  return screen.policies[p] ?? p
}

/** A refusal code in words, with the view's numbers filled in. */
export function refusalText(code: string, view?: Pick<CommissionView, 'minStanding' | 'maxPerSystem' | 'capacity' | 'charterPrice'>): string {
  const t = screen.refusals[code]
  if (t === undefined) return ''
  if (view === undefined) return t
  return t
    .replace('{min}', String(view.minStanding))
    .replace('{max}', String(view.maxPerSystem))
    .replace('{cap}', String(view.capacity))
    .replace('{price}', formatCr(view.charterPrice))
}

/** Steps a choice list by `delta`, wrapping. */
export function cycle<T>(list: readonly T[], current: T, delta: 1 | -1): T {
  const i = list.indexOf(current)
  const n = list.length
  return list[(((i < 0 ? 0 : i) + delta) % n + n) % n]
}

// ---------------------------------------------------------------------------------------------
// Charter
// ---------------------------------------------------------------------------------------------

export type CharterBlock = 'notFounded' | 'standingTooLow' | 'systemFull' | 'capReached' | 'noFunds'

export interface CharterModel {
  canBuy: boolean
  block: CharterBlock | null
  reason: string
  /** True when the holder's standing rule applies (held by another faction). */
  needsStanding: boolean
}

/** Whether a charter in `system` can be bought now (`buyCharter`'s refusals in its order). */
export function charterModel(view: CommissionView, system: CommissionSystemView, standing: number): CharterModel {
  const needsStanding = system.holder !== null && system.holder !== 'player'
  let block: CharterBlock | null = null
  if (!view.founded) block = 'notFounded'
  else if (needsStanding && standing < view.minStanding) block = 'standingTooLow'
  else if (system.charters + system.stations >= view.maxPerSystem) block = 'systemFull'
  else if (view.used >= view.capacity) block = 'capReached'
  else if (!(view.treasury >= view.charterPrice)) block = 'noFunds'
  return { canBuy: block === null, block, reason: block === null ? '' : refusalText(block, view), needsStanding }
}

export function charterCommand(systemId: string, standing: number): Omit<Extract<M6Command, { type: 'buyCharter' }>, 'wallet'> {
  return { type: 'buyCharter', systemId, standing }
}

// ---------------------------------------------------------------------------------------------
// Commission
// ---------------------------------------------------------------------------------------------

/** The approved sites of `system` still free. */
export function freeSites(system: Pick<CommissionSystemView, 'sites'>): CommissionSiteView[] {
  return system.sites.filter((s) => !s.used)
}

/** The levels type `type` offers (empty when the type is not offered). */
export function levelsOf(view: Pick<CommissionView, 'types'>, type: StationTypeName): readonly CommissionLevelView[] {
  return view.types.find((t) => t.type === type)?.levels ?? []
}

/** Live commissions over the whole Reach. */
export function stationCount(view: Pick<CommissionView, 'systems'>): number {
  let n = 0
  for (const s of view.systems) n += s.stations
  return n
}

/** "1 station", "3 stations" (D-459 nit). */
export function stationCountLabel(n: number): string {
  return (n === 1 ? screen.stationCount.one : screen.stationCount.many).replace('{n}', String(n))
}

/**
 * A station's P&L in words: today's figure (the commission's `earnedToday`, cleared at each faction day rollover)
 * and the running total since commissioning (`earnedTotal`), each labelled, so a total that keeps growing is never
 * read as today's (D-459 bug 9).
 */
export function stationEarnings(v: Pick<PlayerStationView, 'earnedToday' | 'earnedTotal'>): string {
  return screen.earnings.replace('{today}', formatCr(v.earnedToday)).replace('{total}', formatCr(v.earnedTotal))
}

export interface CommissionDraft {
  systemId: string
  site: number | null
  type: StationTypeName
  level: number
  autoBuy: boolean
}

export type CommissionBlock = 'notFounded' | 'noCharter' | 'capReached' | 'badLevel' | 'noSite'

export interface CommissionModel {
  canCommission: boolean
  block: CommissionBlock | null
  reason: string
  /** The chosen level's cost and build days, null for a level the type doesn't offer. */
  level: CommissionLevelView | null
  sites: CommissionSiteView[]
}

/** Whether `draft` can be commissioned now (`commissionStation`'s refusals in its order). */
export function commissionModel(view: CommissionView, draft: CommissionDraft): CommissionModel {
  const system = view.systems.find((s) => s.systemId === draft.systemId)
  const level = levelsOf(view, draft.type).find((l) => l.level === draft.level) ?? null
  const sites = system === undefined ? [] : freeSites(system)
  let block: CommissionBlock | null = null
  if (!view.founded) block = 'notFounded'
  else if (system === undefined || system.charters === 0) block = 'noCharter'
  else if (stationCount(view) >= view.capacity) block = 'capReached'
  else if (level === null) block = 'badLevel'
  else if (draft.site === null || !sites.some((s) => s.index === draft.site)) block = 'noSite'
  return { canCommission: block === null, block, reason: block === null ? '' : refusalText(block, view), level, sites }
}

export function commissionCommand(draft: CommissionDraft & { site: number }): Extract<M6Command, { type: 'commissionStation' }> {
  return { type: 'commissionStation', systemId: draft.systemId, site: draft.site, stationType: draft.type, level: draft.level, autoBuy: draft.autoBuy }
}

/** Keeps a draft valid after the type or system changes: a level the type offers, a free site or none. */
export function tidyDraft(view: CommissionView, draft: CommissionDraft): CommissionDraft {
  const levels = levelsOf(view, draft.type)
  const top = levels.length > 0 ? levels[levels.length - 1].level : 1
  const level = Math.min(Math.max(1, draft.level), top)
  const system = view.systems.find((s) => s.systemId === draft.systemId)
  const free = system === undefined ? [] : freeSites(system)
  const site = draft.site !== null && free.some((s) => s.index === draft.site) ? draft.site : (free[0]?.index ?? null)
  return { ...draft, level, site }
}

/** The systems with a charter waiting for a commission. */
export function chartersWaiting(view: Pick<CommissionView, 'systems'>): CommissionSystemView[] {
  return view.systems.filter((s) => s.charters > 0)
}

// ---------------------------------------------------------------------------------------------
// Construction and upgrades
// ---------------------------------------------------------------------------------------------

export interface ConstructionModel {
  /** 0..1 of the wares delivered. */
  supplied: number
  stage: string
  /** "3 of 5 days". */
  days: string
  missing: { ware: WareId; tonnes: number }[]
}

/** The construction progress of a station being built or upgraded, else null. */
export function constructionModel(v: Pick<PlayerStationView, 'construction'>): ConstructionModel | null {
  const site = v.construction
  if (site === null) return null
  let req = 0
  let got = 0
  const missing: ConstructionModel['missing'] = []
  for (const id of Object.keys(site.required) as WareId[]) {
    const r = site.required[id] ?? 0
    const d = Math.min(r, site.delivered[id] ?? 0)
    req += r
    got += d
    if (r - d > 0) missing.push({ ware: id, tonnes: r - d })
  }
  return {
    supplied: req > 0 ? got / req : 1,
    stage: site.stage,
    days: `${Math.floor(site.progressDays)} of ${site.buildDays} days`,
    missing,
  }
}

export type UpgradeBlock = 'notBuilt' | 'building' | 'maxLevel'

/** Whether station `v` can start its next level (`upgradeStation`'s refusals). */
export function upgradeModel(v: Pick<PlayerStationView, 'level' | 'construction' | 'upgrade'>): { canUpgrade: boolean; block: UpgradeBlock | null; reason: string } {
  let block: UpgradeBlock | null = null
  if (v.level === 0) block = 'notBuilt'
  else if (v.construction !== null) block = 'building'
  else if (v.upgrade === null) block = 'maxLevel'
  return { canUpgrade: block === null, block, reason: block === null ? '' : refusalText(block) }
}

// ---------------------------------------------------------------------------------------------
// Price policy and permissions
// ---------------------------------------------------------------------------------------------

export interface PriceLimitDraft {
  ware: WareId
  /** 0 = no limit on that side. */
  min: number
  max: number
}

export interface PolicyDraft {
  policy: PricePolicyName
  limits: PriceLimitDraft[]
}

/**
 * The policy draft of station `v`: its policy, and one limit row per ware it trades (its market rows), filled
 * from the view's limits (no limit = 0). Wares with a limit but no market row are kept.
 */
export function policyDraft(v: Pick<PlayerStationView, 'pricePolicy' | 'priceLimits' | 'station'>): PolicyDraft {
  const set = new Map(v.priceLimits.map((l) => [l.ware, l]))
  const wares: WareId[] = (v.station?.wares ?? []).map((r) => r.ware)
  for (const l of v.priceLimits) if (!wares.includes(l.ware)) wares.push(l.ware)
  return {
    policy: v.pricePolicy,
    limits: wares.map((ware) => ({ ware, min: set.get(ware)?.min ?? 0, max: set.get(ware)?.max ?? 0 })),
  }
}

/** Steps one limit by `delta` × `PRICE_STEP` (never below 0). */
export function stepLimit(draft: PolicyDraft, ware: WareId, side: 'min' | 'max', delta: number): PolicyDraft {
  return {
    ...draft,
    limits: draft.limits.map((l) => (l.ware === ware ? { ...l, [side]: Math.max(0, l[side] + delta * PRICE_STEP) } : l)),
  }
}

/** Why the draft can't be saved ('' when it can): a min above its max, or a bad number. */
export function policyBlock(draft: PolicyDraft): string {
  if (draft.policy === 'auto') return ''
  for (const l of draft.limits) {
    if (!Number.isFinite(l.min) || !Number.isFinite(l.max) || l.min < 0 || l.max < 0) return refusalText('badLimit')
    if (l.min > 0 && l.max > 0 && l.min > l.max) return refusalText('badLimit')
  }
  return ''
}

/** The `setPricePolicy` command: auto sends no limits; manual sends the wares with a limit (0 → null). */
export function policyCommand(commissionId: string, draft: PolicyDraft): Extract<M6Command, { type: 'setPricePolicy' }> {
  const limits =
    draft.policy === 'auto'
      ? []
      : draft.limits.filter((l) => l.min > 0 || l.max > 0).map((l) => ({ ware: l.ware, min: l.min > 0 ? l.min : null, max: l.max > 0 ? l.max : null }))
  return { type: 'setPricePolicy', commissionId, policy: draft.policy, limits }
}

export function nextPolicy(p: PricePolicyName, delta: 1 | -1): PricePolicyName {
  return cycle(PRICE_POLICY_NAMES, p, delta)
}

export function nextPermission(p: TradePermissionName, delta: 1 | -1): TradePermissionName {
  return cycle(TRADE_PERMISSION_NAMES, p, delta)
}

export function permissionCommand(commissionId: string, permission: TradePermissionName): Extract<M6Command, { type: 'setPermissions' }> {
  return { type: 'setPermissions', commissionId, permission }
}

// ---------------------------------------------------------------------------------------------
// Manager
// ---------------------------------------------------------------------------------------------

export interface ManagerChoice {
  captainId: string | null
  label: string
}

/**
 * The manager picker for station `commissionId`: *No manager* when it has one, then every hired captain fit to
 * manage (state `hired`, not this station's manager already). A captain with another job is offered with it
 * named, since `setManager` moves it (one job per captain).
 */
export function managerChoices(
  commissionId: string,
  current: CaptainSummary | null,
  hired: readonly CaptainSummary[],
  jobName: (c: CaptainSummary) => string | null,
): ManagerChoice[] {
  const out: ManagerChoice[] = []
  if (current !== null) out.push({ captainId: null, label: 'No manager' })
  for (const c of hired) {
    if (c.state !== 'hired' || c.stationId === commissionId) continue
    const job = jobName(c)
    out.push({ captainId: c.id, label: `${c.name} · Trade ${c.skills.trade}, Leadership ${c.skills.leadership}${job === null ? '' : ` (leaves: ${job})`}` })
  }
  return out
}

export function managerCommand(commissionId: string, captainId: string | null): Extract<M6Command, { type: 'setManager' }> {
  return { type: 'setManager', commissionId, captainId }
}

export function upgradeCommand(commissionId: string): Extract<M6Command, { type: 'upgradeStation' }> {
  return { type: 'upgradeStation', commissionId }
}

// ---------------------------------------------------------------------------------------------
// Claims: tariff and docking rights
// ---------------------------------------------------------------------------------------------

/** Clamps a manual tariff to corp.json `claims.tariff` (the worker's `clampTariff`). */
export function clampTariff(t: number): number {
  const [min, max] = TARIFF_RANGE
  return t < min ? min : t > max ? max : t
}

/** Steps a tariff draft (null = automatic): from automatic a step starts at the range's low end. */
export function stepTariff(t: number | null, delta: 1 | -1): number {
  if (t === null) return TARIFF_RANGE[0]
  return clampTariff(Math.round((t + delta * TARIFF_STEP) * 10000) / 10000)
}

/** A manual tariff in percent, or "not set" for an automatic one (it charges 0 %: D-424, M5 spec §27.6). */
export function tariffLabel(t: number | null): string {
  return t === null ? screen.tariffUnset : formatPct(t)
}

export function tariffCommand(systemId: string, tariff: number | null): Extract<M6Command, { type: 'setClaimTariff' }> {
  return { type: 'setClaimTariff', systemId, tariff: tariff === null ? null : clampTariff(tariff) }
}

export function nextDocking(d: DockingRightsName, delta: 1 | -1): DockingRightsName {
  return cycle(DOCKING_RIGHTS_NAMES, d, delta)
}

export function dockingCommand(systemId: string, rights: DockingRightsName): Extract<M6Command, { type: 'setDockingRights' }> {
  return { type: 'setDockingRights', systemId, rights }
}

/** The worker's refusal codes for station and claim commands, in words (null = not one of these). */
export function stationRefusal(code: string): string | null {
  const map: Readonly<Record<string, string>> = {
    noCharter: refusalText('noCharter'),
    standingTooLow: 'The claim holder refuses: your standing is too low.',
    capReached: 'A station or charter cap is reached.',
    noFunds: 'The company account is short.',
    notAllowed: 'That site is taken.',
    maxLevel: refusalText('maxLevel'),
    busy: 'Busy: construction is running, or the captain is recovering or on strike.',
    notOwned: refusalText('notHeld'),
    unknownCommission: 'That station is gone.',
  }
  return map[code] ?? null
}
